import type { ComponentPropsWithoutRef, ElementRef, ReactElement } from 'react';
import { forwardRef } from 'react';
import { Button } from '@bice_vida/ds-components-button';
import { cx } from 'class-variance-authority';

import '@bice_vida/ds-styles-step-progress-bar';

import styles from './StepProgressBar.module.scss';

export type StepProgressBarProps = {
  /**
   * Custom ClassName for container layout
   **/
  containerClassName?: string;
  /**
   * Custom ClassName for stepper
   **/
  stepperClassName?: string;
  /**
   * Custom ClassName for progress bar
   **/
  progressBarClassName?: string;
  /**
   * Custom BottomBar component
   **/
  customBottomBar?: ReactElement;
  /**
   * Steps of flow
   **/
  steps: number;
  /**
   * Current step number
   **/
  currentStep: number;
  /**
   * Title of step
   **/
  title?: string;
  /**
   * Subitle of step
   **/
  subtitle?: string;
  /**
   * Disable on Continue button
   **/
  disabled?: boolean;
  /**
   * Loading of Continue button
   **/
  loading?: boolean;
  /**
   * Function on continue
   **/
  onContinue?: () => void;
  /**
   * Function on back
   **/
  onBack?: () => void;
  /**
   * Custom text of continue button
   **/
  textOnContinueButton?: string;
  /**
   * Custom text of back button
   **/
  textOnBackButton?: string;
  /**
   * Show back button
   **/
  showBackButton?: boolean;
} & ComponentPropsWithoutRef<'div'>;

const StepProgressBar = forwardRef<ElementRef<'div'>, StepProgressBarProps>(
  (
    {
      containerClassName,
      stepperClassName,
      progressBarClassName,
      customBottomBar,
      steps,
      currentStep,
      onContinue,
      onBack,
      title,
      subtitle,
      disabled = false,
      loading = false,
      textOnContinueButton = 'Continuar',
      textOnBackButton = 'Volver',
    },
    ref
  ) => {
    const containerClassNames: string = cx('step-progress-bar', containerClassName);
    const stepperClassNames: string = cx('step-progress-bar__stepper', stepperClassName);
    const contentClassName: string = cx('step-progress-bar__content');
    const textsClassNames: string = cx('step-progress-bar__content__texts');
    const titleClassNames: string = cx('step-progress-bar__content__texts__title');
    const subtitleClassNames: string = cx('step-progress-bar__content__texts__subtitle');
    const buttonsClassName: string = cx('step-progress-bar__content__buttons');

    const progressBar = Array.from({ length: steps }, (_, index) => index + 1);

    return (
      <div className={containerClassNames} data-testid="step-progress-bar-props-testid" ref={ref}>
        <div className={stepperClassNames}>
          {progressBar.map((value) => (
            <div
              className={cx('step-progress-bar__stepper__progress_bar', progressBarClassName, {
                'step-progress-bar__stepper__progress_bar__active': value <= currentStep,
              })}
              key={`${value}`}
            />
          ))}
        </div>
        <div className={`${contentClassName} ${styles['content_buttons']}`}>
          <div className={`${textsClassNames} ${styles['content_text']}`}>
            <p className={titleClassNames}>{title}</p>
            <p className={subtitleClassNames}>{subtitle}</p>
          </div>
          <div className={buttonsClassName}>
            {currentStep !== 1 && (
              <Button className={styles['back_button']} onClick={onBack} size="l" variant="link">
                {textOnBackButton}
              </Button>
            )}
            <Button disabled={disabled} loading={loading} onClick={onContinue} size="l">
              {textOnContinueButton}
            </Button>
          </div>
        </div>
        {customBottomBar}
      </div>
    );
  }
);

StepProgressBar.displayName = 'StepProgressBar';

export default StepProgressBar;
